import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { Button, Card, Image, Label, Icon, Dropdown } from 'semantic-ui-react';
import i18next from 'i18next';
import PrimaryCategories from './PrimaryCategories';
import MenuSubcategory from './MenuSubcategory';
import MenuItem from './MenuItem';
import SetSubcategory from './SetSubcategory';
import SetMenuItem from './SetMenuItem';
import { setEditedEntity, confirmDelete, saveMenuCategory, deleteMenuCategory, confirm, deleteOverrides } from '../../../actions';
import { getMenuSubcategoriesAppearances, getBusinessCurrency } from '../../../selectors';
import pizzaIcon from '../../../assets/icons/pizza.svg';
import bubblesIcon from '../../../assets/icons/bubbles.svg';
import dealIcon from '../../../assets/icons/discount.svg';

const catTypeColors = { 'primary': 'blue', 'mandatory': 'green', 'addon': 'yellow' };

const getItemStyle = (isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: "none",
    padding: 10,

    // change background colour if dragging
    // background: "lightgray",

    // styles we need to apply on draggables
    ...draggableStyle
});

const getListStyle = isDraggingOver => ({
    minHeight: 60,
});

class MenuCategory extends Component {
    state = { showItems: this.props.category.type === 'primary', showCategories: true }

    handleCategoryUpdate = (updates) => {
        const { category, branchIdForOverride } = this.props;
        this.props.saveMenuCategory(category.menuId, branchIdForOverride, updates);
    }

    handleCategoryDelete = () => {
        const { category, branchIdForOverride } = this.props;
        this.props.confirmDelete(category.name, () => {
            this.props.deleteMenuCategory(category.menuId, branchIdForOverride, category.id);
        });
    }

    getChildCategories = category => {
        return category.categories.map(childCategory => {
            return childCategory.categories ? { ...childCategory, childCategories: this.getChildCategories(childCategory) } : childCategory;
        });
    }

    handleCategoryDuplicate = () => {
        const { category } = this.props;

        this.props.setEditedEntity('menuCategory', {
            ...category,
            id: undefined,
            itemsInstancesToAdd: category.itemsInstances.map(({ id, ...i }) => i)
        }, { childCategories: category.categories ? this.getChildCategories(category) : null });
    }

    handleReorder = () => {
        const { category, itemsById } = this.props;

        const itemsInstances = Array.from(category.itemsInstances);

        const itemsInstancesToUpdate = itemsInstances
            .sort((i1, i2) => itemsById[i1.itemId].name.localeCompare(itemsById[i2.itemId].name))
            .map((item, index) => ({ id: item.id, sortIndex: index + 1 }));

        this.handleCategoryUpdate({ id: category.id, itemsInstancesToUpdate });
    }

    handlePriceChange = () => {
        setTimeout(() => {
            const result = prompt(i18next.t('menu.overridePrice'));

            if (result !== null && result >= 0) {
                const { category } = this.props;
                const itemsInstancesToUpdate = Array.from(category.itemsInstances).map(item => ({ id: item.id, price: Number(result) }));

                this.handleCategoryUpdate({ id: category.id, itemsInstancesToUpdate });
            }
        }, 100);
    }

    handleSubcategorySetup = (subcategory) => {
        const { category } = this.props;

        if (subcategory.type === 'primary') {
            this.handleCategoryUpdate({ id: subcategory.id, primaryParentId: category.id });
        } else {
            this.handleCategoryUpdate({ id: category.id, subcategoriesIds: category.subcategoriesIds.concat(subcategory.id) });
        }
    }

    handleSubcategoryUnset = (subcategoryId) => {
        const { category } = this.props;
        this.handleCategoryUpdate({ id: category.id, subcategoriesIds: category.subcategoriesIds.filter(subId => subId !== subcategoryId) });
    }

    handleItemSetup = (item) => {
        const { category } = this.props;
        const { id: itemId, price } = item;
        const sortIndex = category.items.length + 1;

        let itemsInstancesToAdd = [{ itemId, sortIndex }];

        if (category.options.builderType === 'pizza') {
            itemsInstancesToAdd = [
                { itemId, sortIndex, meta: { pizzaCover: 0 } },
                { itemId, price: price / 2, sortIndex, meta: { pizzaCover: 1 } },
                { itemId, price: price / 2, sortIndex, meta: { pizzaCover: 2 } }
            ];
        }

        this.handleCategoryUpdate({ id: category.id, itemsInstancesToAdd });
    }

    handleItemUpdate = ({ id, price, posItemId, itemId, meta }) => {
        const { category } = this.props;
        const item = this.props.itemsById[itemId];
        const itemInstance = { id, price, posItemId, meta };

        this.props.setEditedEntity('menuItem', item, {
            itemInstance,
            updateItemInstance: updatedInstance => {
                this.handleCategoryUpdate({ id: category.id, itemsInstancesToUpdate: [updatedInstance] });
            }
        });
    }

    handleOverrideClick = (category) => {
        const { confirm, deleteOverrides, branchIdForOverride } = this.props;

        confirm({
            title: i18next.t('menu.syncOverrideTitle'),
            message: `${i18next.t('menu.syncOverrideMessage')}: ${Object.keys(category.branchOverrides).join(', ')}`,
            onConfirm: () => {
                deleteOverrides(category.menuId, branchIdForOverride, 'category', category.id);
            }
        });
    }

    handleItemUnset = (item) => {
        const { category } = this.props;
        const itemsInstancesToRemove = item.meta && item.meta.pizzaCover >= 0 ? category.items.filter(i => i.itemId === item.itemId).map(i => i.id) : [item.id];
        this.handleCategoryUpdate({ id: category.id, itemsInstancesToRemove });
    }

    render() {
        const { category, index } = this.props;

        const appearances = ['addon', 'mandatory'].includes(category.type) ? (this.props.subcategoriesAppearances[category.id] || []) : [];
        const preventDelete = appearances.length > 0;

        return (
            <Draggable draggableId={`category-${category.id}`} index={index}>
                {(provided, snapshot) => (
                    <div
                        {...provided.draggableProps}
                        ref={provided.innerRef}
                        id={`category:${category.id}`}
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                    >
                        <Card style={{ width: 'auto', minWidth: 290, marginBottom: category.type !== 'primary' ? 0 : 100 }}>
                            <Card.Content {...provided.dragHandleProps}>
                                <div className="ui right floated">
                                    {category.primaryParentId ? <Button circular size='mini' icon='external' basic onClick={() => this.handleCategoryUpdate({ id: category.id, primaryParentId: null })} /> : null}
                                    <Button circular size='mini' icon='write' basic onClick={() => this.props.setEditedEntity('menuCategory', category)} />
                                    <Dropdown
                                        trigger={<Button circular size='mini' icon='ellipsis vertical' basic />}
                                        options={[
                                            { key: 'duplicate', text: i18next.t('menu.duplicateCategory'), onClick: this.handleCategoryDuplicate },
                                            { key: 'reorder', text: i18next.t('menu.reorderAlphabetically'), onClick: this.handleReorder },
                                            { key: 'price', text: i18next.t('menu.overrideSubitemsPrice'), onClick: this.handlePriceChange },
                                            { key: 'delete', text: i18next.t('delete'), onClick: preventDelete ? () => null : this.handleCategoryDelete, disabled: preventDelete },
                                        ]}
                                        pointing='top right'
                                        icon={null}
                                    />
                                </div>
                                <Image className={category.imageUrl ? 'zoom' : ''} floated='left' size='mini' src={category.imageUrl || 'https://react.semantic-ui.com/images/wireframe/image.png'} />
                                <Card.Header>{category.name}</Card.Header>
                                <Card.Meta className="meta-overflow">{category.description}</Card.Meta>
                                <Card.Description>
                                    {appearances.length ? <Label basic className="tooltip">{appearances.length}<span className="tooltiptext">{appearances.map((value, index) => <div key={index}>{value}</div>)}</span></Label> : null}
                                    <Label>{category.key}</Label>
                                    <Label color={catTypeColors[category.type]}>
                                        {category.primaryParentId && category.options.spreadItemsIfPossible && <Icon name='eye slash outline' />}
                                        {category.options.supportedFor &&
                                            <span>
                                                {category.options.supportedFor.includes('sitting') ? <Icon name='food' /> : null}
                                                {category.options.supportedFor.includes('takeaway') ? <Icon name='shopping bag' /> : null}
                                                {category.options.supportedFor.includes('delivery') ? <Icon name='motorcycle' /> : null}
                                            </span>
                                        }
                                        {i18next.t(`menu.${category.type}`)}
                                    </Label>
                                    {!category.isAvailable ? <Label floated="right" color="red">{i18next.t('inactive')}</Label> : null}
                                    {category.options.builderType === 'pizza' ? <img alt="pizza" title="Pizza" src={pizzaIcon} style={{ width: 25, margin: 1 }} /> : null}
                                    {category.options.builderType === 'bubbles' ? <img alt="bubbles" title="Bubbles" src={bubblesIcon} style={{ width: 25, margin: 1 }} /> : null}
                                    {category.options.deal ? <img alt="deal" title="deal" src={dealIcon} style={{ width: 25, margin: 1 }} /> : null}
                                    {category.options.supportedCustomerTypes && category.options.supportedCustomerTypes.length > 0 ? <Icon className="right floated" name='briefcase' size="large" color={category.options.supportedCustomerTypes.includes('employee') ? 'green' : 'red'} /> : null}
                                    {category.branchOverrides ? <div className="right floated tooltip" style={{ borderBottom: 'none' }}><Icon size="large" name='warning sign' color="yellow" onClick={() => this.handleOverrideClick(category)} /><span className="tooltiptext">{Object.keys(category.branchOverrides).join(' | ')}</span></div> : null}
                                </Card.Description>
                                <Card.Description style={{ fontStyle: 'italic' }}>{category.note}</Card.Description>
                            </Card.Content>
                            {category.type !== 'primary' ? null :
                                <Card.Content extra>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <SetSubcategory
                                            category={category}
                                            onNewSelected={(name) => this.props.setEditedEntity('menuCategory', { menuId: category.menuId, name }, { linkTo: this.handleSubcategorySetup })}
                                            onItemSelected={this.handleSubcategorySetup}
                                            
                                        />
                                        {!category.isPrimaryParent && !category.subcategories.length ? null :
                                            <Button
                                                circular
                                                basic
                                                icon
                                                onClick={() => this.setState({ showCategories: !this.state.showCategories })}
                                            >
                                                {(category.isPrimaryParent ? category.categories.length : category.subcategories.length) + ' '}
                                                <Icon name={this.state.showCategories ? 'chevron up' : 'chevron down'} />
                                            </Button>
                                        }
                                    </div>
                                    {!this.state.showCategories ? null :
                                        <Droppable droppableId={`category-${category.id}-subcategories`} type="subcategories">
                                            {(provided, snapshot) => (
                                                <div
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                >
                                                    {category.subcategories.map((subcategory, index) => (
                                                        <MenuSubcategory
                                                            key={subcategory.id + index}
                                                            parantId={category.id}
                                                            category={subcategory}
                                                            index={index}
                                                            handleRemove={this.handleSubcategoryUnset}
                                                            handleSubcategoryClick={this.props.handleSubcategoryClick}
                                                            
                                                        />
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    }
                                </Card.Content>
                            }
                            {category.isPrimaryParent && this.state.showCategories ?
                                <PrimaryCategories
                                    parantId={category.id}
                                    categories={category.categories || []}
                                    handleSubcategoryClick={this.props.handleSubcategoryClick}
                                    branchIdForOverride={this.props.branchIdForOverride}
                                    
                                    
                                />
                                : null
                            }
                            {category.isPrimaryParent ? null :
                                <Card.Content extra>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <SetMenuItem
                                            onNewSelected={(name) => this.props.setEditedEntity('menuItem', { menuId: category.menuId, name }, { linkToCategory: this.handleItemSetup })}
                                            onItemSelected={this.handleItemSetup}
                                            
                                        />
                                        {!category.items.length ? null :
                                            <Button
                                                circular
                                                basic
                                                icon
                                                onClick={() => this.setState({ showItems: !this.state.showItems })}
                                            >
                                                {category.items.length + ' '}
                                                <Icon name={this.state.showItems ? 'chevron up' : 'chevron down'} />
                                            </Button>
                                        }
                                    </div>
                                    {!this.state.showItems ? null :
                                        <Droppable droppableId={`${category.id}-items`} type="items">
                                            {(provided, snapshot) => (
                                                <div
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                    style={getListStyle(snapshot.isDraggingOver)}
                                                >
                                                    {category.items.map((item, index) => (
                                                        <MenuItem
                                                            key={item.id}
                                                            item={item}
                                                            index={index}
                                                            
                                                            handleUpdate={this.handleItemUpdate}
                                                            handleRemove={this.handleItemUnset}
                                                            handleSubcategoryClick={this.props.handleSubcategoryClick}
                                                            currencyCode={this.props.currencyCode}
                                                            branchIdForOverride={this.props.branchIdForOverride}
                                                        />
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    }
                                </Card.Content>
                            }
                        </Card>
                    </div>
                )}
            </Draggable>
        );
    }
}

MenuCategory.propTypes = {
    handleSubcategoryClick: PropTypes.func,
    setEditedEntity: PropTypes.func,
    handleRemove: PropTypes.func,
    item: PropTypes.object,
    handleUpdate: PropTypes.func,
    currencyCode: PropTypes.string,
    confirm: PropTypes.func,
    deleteOverrides: PropTypes.func,
    subcategoriesAppearances: PropTypes.object,
    category: PropTypes.object,
    branchIdForOverride: PropTypes.string,
    index: PropTypes.number,
    itemsById: PropTypes.object,
    saveMenuCategory: PropTypes.func,
    confirmDelete: PropTypes.func,
    deleteMenuCategory: PropTypes.func
};

const mapStateToProps = (state) => {
    const { items: itemsById } = state.businessApp.menu;
    const currencyCode = getBusinessCurrency(state);
    const subcategoriesAppearances = getMenuSubcategoriesAppearances(state);

    return { itemsById, subcategoriesAppearances, currencyCode };
};

export default connect(mapStateToProps, {
    setEditedEntity,
    saveMenuCategory,
    deleteMenuCategory,
    confirmDelete,
    confirm,
    deleteOverrides
})(MenuCategory);
